import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import classNames from 'classnames';
import {
  Box,
  parseDiscountData,
  useGetBoxDiscountedPriceString,
  useGetBoxRentalPriceString,
  useGetBoxRentalPriceStringWithVat,
} from 'entities/Box';
import { getWarehouseInfo } from 'pages/BookingPage';
import React, { FC, memo, useCallback, useLayoutEffect, useMemo } from 'react';
import { ReactComponent as CheckMark } from 'shared/assets/icons/CheckMark.svg';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';
import { Button } from 'shared/ui/Button';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { trimPriceFrequency } from '../utils/helpers/trimPriceFrequency';
import { DiscountLabel } from './DiscountLabel';
// import { InvoiceFrequencyType } from 'entities/Invoice';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { UserAccountType } from 'entities/User';
import { WarehouseDiscount } from 'entities/Warehouse';
import { Popup } from 'shared/ui/Popup';
import { FIRST_MONTH_DISCOUNT_REGEX } from 'shared/utils/regex';
import { requestFormModalActions, RequestModalMode } from 'widgets/RequestModal';

interface BoxRowProps {
  box: Box;
  selectedBoxId: string | undefined;
  hasRentDiscounts: boolean;
  hasGeneralDiscounts: boolean;
  onSelectBox: (box: Box) => void;
  onBookBox: (box: Box) => void;
  accountType?: Nullable<UserAccountType>;
}

export const BoxRow: FC<BoxRowProps> = memo((props) => {
  const { box, selectedBoxId, onSelectBox, onBookBox, accountType } = props;

  const { t } = useAppTranslation('booking');
  const dispatch = useAppDispatch();
  const { scrollToElement, scrollRef } = useScrollToElement('center', 500);
  const { isDesktop } = useScreenBreakpoints();
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const globalSettings = useAppSelector(getGlobalSettings);

  const showPriceWithVat = globalSettings?.showPriceWithVat;
  const discountedPrice = useGetBoxDiscountedPriceString({ warehouseInfo, boxInfo: box, showPriceWithVat, accountType });

  const currencySymbol = useGetCurrencySymbol();

  const discountedPriceText = discountedPrice?.split(currencySymbol);

  const firstMonthDiscount = useMemo((): WarehouseDiscount | undefined => {
    if (!warehouseInfo?.warehouseDiscounts) {
      return;
    }

    const sortedDiscounts = [...warehouseInfo.warehouseDiscounts].sort((a, b) => a.priority - b.priority);

    const actualDiscount = sortedDiscounts?.find((discount) => {
      const hasUnitSizes = Boolean(discount.unitSizes?.length);

      const includesFirstMonth = discount.discounts.split(',').some((discount) => FIRST_MONTH_DISCOUNT_REGEX.test(discount.trim()));

      if (hasUnitSizes) {
        const unitSizes = discount.unitSizes.split(',').map((size) => Number(size));

        const includesSize = unitSizes?.includes(box.sizeCode.square);

        return includesFirstMonth && includesSize;
      }

      return includesFirstMonth;
    });

    return actualDiscount;
  }, [box.sizeCode.square, warehouseInfo?.warehouseDiscounts]);

  const parsedDiscount = useMemo(() => {
    return parseDiscountData(firstMonthDiscount);
  }, [firstMonthDiscount]);

  useLayoutEffect(() => {
    selectedBoxId === box.boxId && scrollToElement();
  }, [box.boxId, scrollToElement, selectedBoxId]);

  const discount = box.priceFactor < 1 ? 100 - box.priceFactor * 100 : null;
  const isSelected = selectedBoxId === box.boxId;

  const rentalBoxPriceWithVat = useGetBoxRentalPriceStringWithVat(box, InvoiceFrequencyType.MONTH, warehouseInfo, accountType);

  const rentalBoxPrice = useGetBoxRentalPriceString(box);

  const rentalPrice = showPriceWithVat ? rentalBoxPriceWithVat : rentalBoxPrice;

  // const weekRate = useGetBoxRentalPriceString(box, InvoiceFrequencyType.WEEK);

  const handleSelectBox = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.stopPropagation();
      e.preventDefault();

      onSelectBox(box);
    },
    [box, onSelectBox],
  );

  const handleBookBox = useCallback((): void => {
    onBookBox(box);
  }, [box, onBookBox]);

  const handleOpenBookTourFormModal = useCallback((): void => {
    dispatch(
      requestFormModalActions.openRequestModal({
        mode: RequestModalMode.BOOK_TOUR,
        sizeCodeId: box.sizeCodeId || box.sizeCode.sizeCodeId,
        boxName: box.name,
        square: box.sizeCode.square,
      }),
    );
  }, [box, dispatch]);

  return (
    <>
      {/* <div className="desktop:hidden">{discount && <DiscountLabel discount={discount} />}</div> */}
      <div ref={scrollRef} className="p-4 desktop:p-0" onClick={handleSelectBox}>
        <div className={classNames('relative p-3 rounded-lg ease-linear duration-100', { 'bg-accentLight': isSelected })}>
          <div className="flex justify-end -mt-3 desktop:absolute desktop:-top-1 desktop:right-4 desktop:mt-0 desktop:z-10">
            <Button theme="clear" className="underline !py-0 leading-none" fontSize="medium" onClick={handleOpenBookTourFormModal}>
              {t('Book a tour')}
            </Button>
          </div>
          <div
            className={classNames(
              'mt-3 desktop:mt-4 flex items-start desktop:items-center space-x-7 justify-between cursor-pointer desktop:px-6',
            )}
          >
            <div className="flex flex-col desktop:basis-1/3">
              <div className="flex text-lg mb-2 items-center gap-2">
                <span className="text-lg font-bold desktop:font-semibold">{box.name}</span>
                {box.timeToOpenLockMS !== null && <span className="text-xs text-accent">({t('External box')})</span>}
              </div>
              {box.subtype && (
                <Popup
                  description={<div className="max-w-[310px] desktop:max-w-[600px]">{getLocalizedString(box?.specialityDescription)}</div>}
                  isDisabled={!getLocalizedString(box?.specialityDescription)}
                >
                  <div className="flex items-center space-x-1.5 w-fit">
                    <InfoIcon width={20} height={20} className="[&>path]:stroke-accent cursor-pointer flex-shrink-0" />
                    <span className="text-sm text-accent font-semibold">{t(box.subtype)}</span>
                  </div>
                </Popup>
              )}
              <div className="flex items-center space-x-1.5">
                <CheckMark className="shrink-0 stroke-1 stroke-accent" />
                <span className="text-xs font-normal text-primaryLight">
                  {`${roundNumber(box.height)}m (${t('H')}) x ${roundNumber(box.width)}m (${t('W')}) x ${roundNumber(box.length)}m (${t(
                    'D',
                  )})`}
                </span>
              </div>
              <div className="flex items-center space-x-1.5">
                <CheckMark className="shrink-0 stroke-1 stroke-accent" />
                <span className="text-xs font-normal text-primaryLight">{`${t('Volume')}: ${roundNumber(
                  box.height * box.width * box.length,
                )} m³`}</span>
              </div>
              {box.floor && (
                <div className="flex items-center space-x-1.5">
                  <CheckMark className="shrink-0 stroke-accent" />
                  <span className="text-xs font-normal text-primaryLight">{getLocalizedString(box.floor.name)}</span>
                </div>
              )}
            </div>
            {/* <div className="hidden desktop:block desktop:basis-1/12 ">{discount && <DiscountLabel discount={discount} />}</div> */}
            {!isDesktop ? (
              <div className="flex flex-col items-end gap-1 shrink-0 mt-4">
                {discountedPrice ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="text-lg font-bold text-primary whitespace-nowrap leading-none">
                        {trimPriceFrequency(`${discountedPriceText?.[0]} ${currencySymbol}/${t('Month')}`)}
                      </span>
                      {(parsedDiscount?.description || discountedPriceText?.[1]) && (
                        <Popup description={<div className="max-w-[310px]">{parsedDiscount?.description}</div>}>
                          <InfoIcon width={20} height={20} className="[&>path]:stroke-primary cursor-pointer flex-shrink-0 -mt-1" />
                        </Popup>
                      )}
                    </div>
                    {discountedPriceText?.[1] && (
                      <span className="text-xs text-primary leading-tight whitespace-nowrap">{discountedPriceText[1].trim()}</span>
                    )}
                    <div className="text-error text-sm line-through leading-tight">{trimPriceFrequency(rentalPrice)}</div>
                  </>
                ) : (
                  <span className="text-lg font-bold text-primary whitespace-nowrap leading-none">{trimPriceFrequency(rentalPrice)}</span>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-end text-right w-full min-w-0 desktop:basis-4/12">
                <div className="flex flex-wrap justify-end items-center gap-x-1 w-full text-base text-primary font-semibold desktop:text-lg wideDesktop:text-2xl">
                  {discountedPrice ? (
                    <>
                      <div className="flex items-center justify-end gap-1 text-sm desktop:text-lg wideDesktop:text-2xl font-semibold text-right">
                        {`${discountedPriceText?.[0]} ${currencySymbol}/${t('Month')}`}

                        {!discountedPriceText?.[1] && (
                          <Popup description={<div className="max-w-[310px] desktop:max-w-[600px]">{parsedDiscount?.description}</div>}>
                            <InfoIcon width={20} height={20} className="[&>path]:stroke-primary cursor-pointer flex-shrink-0 mb-2" />
                          </Popup>
                        )}
                      </div>
                      {discountedPriceText?.[1] && (
                        <div className="flex items-center justify-end">
                          <span className="text-xs desktop:text-sm desktop:p-1">{discountedPriceText[1]}</span>

                          <Popup description={<div className="max-w-[310px] desktop:max-w-[600px]">{parsedDiscount?.description}</div>}>
                            <InfoIcon width={20} height={20} className="[&>path]:stroke-primary cursor-pointer flex-shrink-0 mb-2" />
                          </Popup>
                        </div>
                      )}
                    </>
                  ) : (
                    rentalPrice
                  )}
                </div>
                {discountedPrice && (
                  <div className="w-full text-error text-sm desktop:text-base wideDesktop:text-lg line-through text-right">
                    {rentalPrice}
                  </div>
                )}
              </div>
            )}
            <div className="hidden desktop:flex desktop:justify-center desktop:basis-2/12">
              <Button containerClassName="shrink-0" onClick={handleBookBox}>
                {t('Book now')}
              </Button>
            </div>
          </div>
          <Button containerClassName="mx-2 mt-3 desktop:hidden" onClick={handleBookBox}>
            {t('Book now')}
          </Button>
        </div>
      </div>
    </>
  );
});
